import * as Sentry from '@sentry/react';
import React, { Component, ReactNode } from 'react';
import { ERROR_STRINGS } from '../constants';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch React errors in production
 * Prevents the entire app from crashing when a component throws an error
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI if provided, otherwise use default
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-destructive"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {ERROR_STRINGS.TITLE}
              </h1>
              <p className="text-muted-foreground mb-6">
                {ERROR_STRINGS.DESCRIPTION}
              </p>
            </div>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-destructive-soft rounded-lg text-left">
                <p className="text-sm font-semibold text-destructive mb-2">
                  {ERROR_STRINGS.ERROR_DETAILS_LABEL}
                </p>
                <p className="text-xs text-destructive/80 font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full px-6 py-3 rounded-lg text-white font-medium transition-colors bg-brand-blue hover:bg-primary-hover"
              >
                {ERROR_STRINGS.TRY_AGAIN_BUTTON}
              </button>
              <button
                onClick={() => (globalThis.location.href = '/')}
                className="w-full px-6 py-3 bg-muted hover:bg-muted/80 rounded-lg text-foreground/80 font-medium transition-colors"
              >
                {ERROR_STRINGS.GO_HOME_BUTTON}
              </button>
            </div>

            <p className="mt-6 text-sm text-muted-foreground">
              {ERROR_STRINGS.CONTACT_SUPPORT}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Reusable Error Fallback Components
 * Use these with ErrorBoundary for consistent error handling
 */

interface ErrorFallbackProps {
  readonly title?: string;
  readonly description?: string;
  readonly onReset?: () => void;
  readonly showHomeButton?: boolean;
}

/**
 * Data loading error fallback - for tables, lists, data grids
 * Use when a specific data component fails to load
 *
 * @example
 * <ErrorBoundary fallback={<DataLoadErrorFallback title="Failed to load users" />}>
 *   <UserTable />
 * </ErrorBoundary>
 */
export function DataLoadErrorFallback({
  title = 'Failed to load data',
  description = 'An error occurred while loading the data. Please try again or contact support if the problem persists.',
  onReset,
}: ErrorFallbackProps) {
  return (
    <div className="rounded-lg border border-destructive/20 bg-destructive-soft p-8 text-center">
      <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertCircle className="w-6 h-6 text-destructive" />
      </div>
      <h3 className="text-lg font-semibold text-destructive mb-2">{title}</h3>
      <p className="text-destructive/80 mb-4">{description}</p>
      {onReset && (
        <Button
          onClick={onReset}
          variant="outline"
          className="border-destructive/30 text-destructive hover:bg-destructive/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      )}
    </div>
  );
}

/**
 * Component error fallback - for smaller UI sections
 * Use when a specific component/section fails
 *
 * @example
 * <ErrorBoundary fallback={<ComponentErrorFallback title="Widget error" />}>
 *   <StatsWidget />
 * </ErrorBoundary>
 */
export function ComponentErrorFallback({
  title = 'Something went wrong',
  description = 'This component encountered an error. Please refresh the page.',
  onReset,
}: ErrorFallbackProps) {
  return (
    <div className="rounded-lg border border-warning/20 bg-warning-soft p-6 text-center">
      <div className="mx-auto w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center mb-3">
        <AlertCircle className="w-5 h-5 text-warning" />
      </div>
      <h4 className="text-base font-semibold text-warning mb-1">{title}</h4>
      <p className="text-sm text-warning/80 mb-3">{description}</p>
      {onReset && (
        <Button
          onClick={onReset}
          size="sm"
          variant="outline"
          className="border-warning/30 text-warning hover:bg-warning/10"
        >
          <RefreshCw className="w-3 h-3 mr-1.5" />
          Retry
        </Button>
      )}
    </div>
  );
}

/**
 * Page error fallback - for full page errors
 * Use when an entire page/route fails
 *
 * @example
 * <ErrorBoundary fallback={<PageErrorFallback />}>
 *   <VoiceJournalPage />
 * </ErrorBoundary>
 */
export function PageErrorFallback({
  title = 'Oops! Something went wrong',
  description = 'We encountered an error while loading this page. Please try again.',
  onReset,
  showHomeButton = true,
}: ErrorFallbackProps) {
  const handleGoHome = () => {
    globalThis.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">{title}</h1>
          <p className="text-muted-foreground mb-6">{description}</p>
        </div>

        <div className="space-y-3">
          {onReset && (
            <Button
              onClick={onReset}
              className="w-full bg-primary hover:bg-primary-hover"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          )}
          {showHomeButton && (
            <Button onClick={handleGoHome} variant="outline" className="w-full">
              <Home className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          )}
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          If the problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}

/**
 * Inline error fallback - for small inline errors
 * Use for form fields or small UI elements
 *
 * @example
 * <ErrorBoundary fallback={<InlineErrorFallback title="Profile error" />}>
 *   <UserProfile />
 * </ErrorBoundary>
 */
export function InlineErrorFallback({
  title = 'Error',
  description = 'Something went wrong',
}: ErrorFallbackProps) {
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive-soft border border-destructive/20">
      <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-destructive">{title}</p>
        {description && (
          <p className="text-xs text-destructive/80 mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Custom error fallback wrapper for advanced use cases
 *
 * @example
 * <ErrorBoundary fallback={
 *   <CustomErrorFallback
 *     icon={<Icon />}
 *     title="Custom Error"
 *     actions={<Button>Retry</Button>}
 *   />
 * }>
 *   <Component />
 * </ErrorBoundary>
 */
interface CustomErrorFallbackProps {
  readonly icon?: ReactNode;
  readonly title: string;
  readonly description?: string;
  readonly actions?: ReactNode;
  readonly variant?: 'error' | 'warning' | 'info';
}

export function CustomErrorFallback({
  icon,
  title,
  description,
  actions,
  variant = 'error',
}: CustomErrorFallbackProps) {
  const variantStyles = {
    error: {
      container: 'border-destructive/20 bg-destructive-soft',
      iconBg: 'bg-destructive/10',
      iconText: 'text-destructive',
      title: 'text-destructive',
      description: 'text-destructive/80',
    },
    warning: {
      container: 'border-warning/20 bg-warning-soft',
      iconBg: 'bg-warning/10',
      iconText: 'text-warning',
      title: 'text-warning',
      description: 'text-warning/80',
    },
    info: {
      container: 'border-primary/20 bg-primary-soft',
      iconBg: 'bg-primary/10',
      iconText: 'text-primary',
      title: 'text-primary',
      description: 'text-primary/80',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`rounded-lg border p-8 text-center ${styles.container}`}>
      {icon && (
        <div
          className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${styles.iconBg}`}
        >
          <div className={styles.iconText}>{icon}</div>
        </div>
      )}
      <h3 className={`text-lg font-semibold mb-2 ${styles.title}`}>{title}</h3>
      {description && (
        <p className={`mb-4 ${styles.description}`}>{description}</p>
      )}
      {actions && <div className="mt-4">{actions}</div>}
    </div>
  );
}
